import ApiRoutes from '@common/defs/api-routes';
import { PracticeDetailed } from '@modules/account/defs/types';
import useApi, { ApiOptions, ApiResponse, FetchApiOptions } from '@common/hooks/useApi';
import { resetEchoInstance } from '@common/plugins/echo';
import { User } from '@modules/users/defs/types';
import useSWR, { KeyedMutator } from 'swr';
import { safeLocalStorage } from '@common/utils/storage';

export interface LoginInput {
  email: string;
  password: string;
  admin?: boolean;
}

export interface RegisterInput {
  email: string;
  password: string;
  fbId?: string;
  refUserId?: number;
  signup_url_parameters?: Record<string, unknown>;
}

export interface RequestPasswordResetInput {
  email: string;
}

export interface ResetPasswordInput {
  email: string;
  password: string;
  passwordConfirmation: string;
  token: string;
}

export interface CheckEmailInput {
  email: string;
}

export interface CheckEmailResponse {
  exists: boolean;
  hasGoogleProvider: boolean;
  hasFacebookProvider: boolean;
  hasEmailProvider: boolean;
  hasPasswordLogin: boolean;
  primaryProvider: string | null;
  availableProviders: string[];
  registrationStep?: string;
  message: string;
}

export interface LinkingApprovalRequest {
  linkingToken: string;
}

export interface LinkingApprovalResponse {
  approvalToken: string;
  userEmail: string;
  newProvider: string;
  existingProviders: string[];
  hasPassword: boolean;
}

export interface ConfirmLinkingInput {
  approvalToken: string;
}

export interface RejectLinkingInput {
  approvalToken: string;
}

export interface ConnectedProvider {
  id: number;
  provider: string;
  providerEmail: string;
  isPrimary: boolean;
  avatarUrl?: string;
}

export interface AuthUrlResponse {
  redirectUrl: string;
  state: string;
}

export interface UpdatePersonalInfoInput {
  firstname: string;
  lastname: string;
  phone: string;
  practices?: number[];
  practicesDetailed?: PracticeDetailed[];
  primaryPractice?: number | null;
}

export interface UpdateAddressInput {
  address: string;
  zip: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  price?: number;
  duration?: number;
}

export interface ValidateResetTokenInput {
  token: string;
  email: string;
}

export interface VerifyEmailInput {
  token: string;
}

export interface OnboardingStatus {
  onboardingStep: string;
  onboardingStartedAt: string | null;
  onboardingCompletedAt: string | null;
  isOnboardingCompleted: boolean;
  registrationStep: string;
  emailVerifiedAt: string | null;
}

export interface UpdateOnboardingStepInput {
  step: string;
}

export interface UpdateOnboardingStepResponse {
  currentStep: string;
  isCompleted: boolean;
  onboardingCompletedAt: string | null;
}

export interface RegistrationProgress {
  registrationStep: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  address?: string;
  zip?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
}

interface AuthData {
  user: User | null;
  login: (
    _input: LoginInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ token: string; user?: User }>>;
  register: (
    _input: RegisterInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ token: string; user?: User }>>;
  checkEmail: (
    _input: CheckEmailInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<CheckEmailResponse>>;
  logout: (_options?: FetchApiOptions) => Promise<ApiResponse<null>>;
  requestPasswordReset: (
    _input: RequestPasswordResetInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<null>>;
  resetPassword: (
    _input: ResetPasswordInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ token: string }>>;
  validateResetToken: (
    _input: ValidateResetTokenInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<null>>;
  verifyEmail: (_input: VerifyEmailInput, _options?: FetchApiOptions) => Promise<ApiResponse<null>>;
  resendVerificationEmail: (_options?: FetchApiOptions) => Promise<ApiResponse<null>>;
  googleAuth: (_options?: FetchApiOptions) => Promise<ApiResponse<AuthUrlResponse>>;
  GetAuthUrl: (
    _provider: string,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<AuthUrlResponse>>;
  requestLinkingApproval: (
    _input: LinkingApprovalRequest,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<LinkingApprovalResponse>>;
  confirmLinking: (
    _input: ConfirmLinkingInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ token: string; user: User }>>;
  rejectLinking: (
    _input: RejectLinkingInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<null>>;
  getConnectedProviders: (_options?: FetchApiOptions) => Promise<
    ApiResponse<{
      providers: ConnectedProvider[];
      hasPassword: boolean;
      primaryProvider: ConnectedProvider;
    }>
  >;
  disconnectProvider: (
    _provider: string,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ user: User }>>;
  getRegistrationProgress: (
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<RegistrationProgress>>;
  updatePersonalInfo: (
    _input: UpdatePersonalInfoInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ user: User; registrationStep: string }>>;
  updateAddress: (
    _input: UpdateAddressInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<{ user: User; registrationStep: string; onboardingStep: string }>>;
  updateOnboardingStep: (
    _input: UpdateOnboardingStepInput,
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<UpdateOnboardingStepResponse>>;
  skipOnboardingStep: (
    _options?: FetchApiOptions
  ) => Promise<ApiResponse<UpdateOnboardingStepResponse>>;
  initialized: boolean;
  mutate?: KeyedMutator<User | null>;
}

const useAuth = (): AuthData => {
  const authEnabled = process.env.NEXT_PUBLIC_AUTH_ENABLED === 'true';
  if (!authEnabled) {
    return {
      initialized: true,
      user: null,
      login: async () => ({ success: false, errors: ['Auth is disabled'] }),
      register: async () => ({ success: false, errors: ['Auth is disabled'] }),
      checkEmail: async () => ({ success: false, errors: ['Auth is disabled'] }),
      logout: async () => ({ success: false, errors: ['Auth is disabled'] }),
      requestPasswordReset: async () => ({ success: false, errors: ['Auth is disabled'] }),
      resetPassword: async () => ({ success: false, errors: ['Auth is disabled'] }),
      validateResetToken: async () => ({ success: false, errors: ['Auth is disabled'] }),
      verifyEmail: async () => ({ success: false, errors: ['Auth is disabled'] }),
      resendVerificationEmail: async () => ({ success: false, errors: ['Auth is disabled'] }),
      googleAuth: async () => ({ success: false, errors: ['Auth is disabled'] }),
      GetAuthUrl: async () => ({ success: false, errors: ['Auth is disabled'] }),
      requestLinkingApproval: async () => ({ success: false, errors: ['Auth is disabled'] }),
      confirmLinking: async () => ({ success: false, errors: ['Auth is disabled'] }),
      rejectLinking: async () => ({ success: false, errors: ['Auth is disabled'] }),
      getConnectedProviders: async () => ({ success: false, errors: ['Auth is disabled'] }),
      disconnectProvider: async () => ({ success: false, errors: ['Auth is disabled'] }),
      getRegistrationProgress: async () => ({ success: false, errors: ['Auth is disabled'] }),
      updatePersonalInfo: async () => ({ success: false, errors: ['Auth is disabled'] }),
      updateAddress: async () => ({ success: false, errors: ['Auth is disabled'] }),
      updateOnboardingStep: async () => ({ success: false, errors: ['Auth is disabled'] }),
      skipOnboardingStep: async () => ({ success: false, errors: ['Auth is disabled'] }),
    };
  }

  const fetchApi = useApi();

  const {
    data: user,
    mutate,
    isLoading,
  } = useSWR<User | null>(
    ApiRoutes.Auth.Me,
    async (url) => {
      if (!safeLocalStorage.getItem('authToken')) {
        return null;
      }
      const options: ApiOptions = {
        method: 'POST',
        displayErrors: false,
      };
      const isAuthRejection = (res: { httpStatus?: number }) =>
        res.httpStatus === 401 || res.httpStatus === 403;

      let response = await fetchApi<{ user: User }>(url, options);
      // Échec transitoire (réseau, timeout, 5xx) : retry avec backoff.
      // On ne jette le token QUE sur un rejet explicite du serveur (401/403),
      // sinon un simple blip réseau déconnectait définitivement l'utilisateur.
      let attempt = 0;
      while (!response.success && !isAuthRejection(response) && attempt < 2) {
        attempt += 1;
        const delayMs = 1000 * attempt;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, delayMs);
        });
        // eslint-disable-next-line no-await-in-loop
        response = await fetchApi<{ user: User }>(url, options);
      }

      if (response.success) {
        return response.data?.user ?? null;
      }
      if (isAuthRejection(response)) {
        safeLocalStorage.removeItem('authToken');
        return null;
      }
      // Toujours en échec après retries : on garde le token et l'utilisateur
      // courant (throw = SWR conserve les données précédentes et re-essaiera).
      throw new Error('auth/me unreachable');
    },
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  const login = async (input: LoginInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{ token: string; user?: User }>(ApiRoutes.Auth.Login, {
      data: input,
      ...options,
    });

    if (response.success && response.data?.token) {
      safeLocalStorage.setItem('authToken', response.data.token);
      await mutate();
    }

    return response;
  };

  const register = async (input: RegisterInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{ token: string; user?: User }>(ApiRoutes.Auth.Register, {
      data: input,
      ...options,
    });

    if (response.success && response.data?.token) {
      safeLocalStorage.setItem('authToken', response.data.token);
      const userData = await mutate();
      if (userData) {
        response.data = { ...response.data, user: userData };
      }
    }

    return response;
  };

  const checkEmail = async (input: CheckEmailInput, options?: FetchApiOptions) => {
    const response = await fetchApi<CheckEmailResponse>(ApiRoutes.Auth.CheckEmail, {
      data: input,
      ...options,
    });
    return response;
  };

  const logout = async (options?: FetchApiOptions) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.Logout, { method: 'POST', ...options });
    safeLocalStorage.removeItem('authToken');
    if (process.env.NEXT_PUBLIC_ENABLE_WS === 'true') {
      resetEchoInstance();
    }
    mutate();
    return response;
  };

  const requestPasswordReset = async (
    input: RequestPasswordResetInput,
    options?: FetchApiOptions
  ) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.RequestPasswordReset, {
      data: input,
      ...options,
    });
    return response;
  };

  const resetPassword = async (input: ResetPasswordInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{ token: string }>(ApiRoutes.Auth.ResetPassword, {
      data: input,
      ...options,
    });
    return response;
  };

  const validateResetToken = async (input: ValidateResetTokenInput, options?: FetchApiOptions) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.ValidateResetToken, {
      data: input,
      ...options,
    });
    return response;
  };

  const verifyEmail = async (input: VerifyEmailInput, options?: FetchApiOptions) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.VerifyEmail, {
      data: input,
      ...options,
    });
    return response;
  };

  const resendVerificationEmail = async (options?: FetchApiOptions) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.ResendVerificationEmail, {
      method: 'POST',
      ...options,
    });
    return response;
  };

  const googleAuth = async (options?: FetchApiOptions) => {
    const response = await fetchApi<AuthUrlResponse>(ApiRoutes.Auth.GoogleRedirect, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const GetAuthUrl = async (provider: string, options?: FetchApiOptions) => {
    const fbId = safeLocalStorage.getItem('auth_fbid');
    const refUserId = safeLocalStorage.getItem('auth_ref');
    const signupUrlParametersRaw = safeLocalStorage.getItem('signup_url_parameters');
    const signupUrlParameters = signupUrlParametersRaw ? JSON.parse(signupUrlParametersRaw) : null;

    const queryParams = new URLSearchParams();
    if (fbId) {
      queryParams.append('fbId', fbId);
    }
    if (refUserId) {
      queryParams.append('refUserId', refUserId);
    }
    if (signupUrlParameters) {
      queryParams.append('signup_url_parameters', JSON.stringify(signupUrlParameters));
    }

    const url = queryParams.toString()
      ? `${ApiRoutes.Auth.GetAuthUrl(provider)}?${queryParams.toString()}`
      : ApiRoutes.Auth.GetAuthUrl(provider);

    const response = await fetchApi<AuthUrlResponse>(url, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const requestLinkingApproval = async (
    input: LinkingApprovalRequest,
    options?: FetchApiOptions
  ) => {
    const response = await fetchApi<LinkingApprovalResponse>(
      ApiRoutes.Auth.RequestLinkingApproval,
      {
        data: input,
        ...options,
      }
    );
    return response;
  };

  const confirmLinking = async (input: ConfirmLinkingInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{ token: string; user: User }>(ApiRoutes.Auth.ConfirmLinking, {
      data: input,
      ...options,
    });

    if (response.success && response.data?.token) {
      safeLocalStorage.setItem('authToken', response.data.token);
      mutate();
      console.log('confirmLinking', response.data);
    }

    return response;
  };

  const rejectLinking = async (input: RejectLinkingInput, options?: FetchApiOptions) => {
    const response = await fetchApi<null>(ApiRoutes.Auth.RejectLinking, {
      data: input,
      ...options,
    });
    return response;
  };

  const getConnectedProviders = async (options?: FetchApiOptions) => {
    const response = await fetchApi<{
      providers: ConnectedProvider[];
      hasPassword: boolean;
      primaryProvider: ConnectedProvider;
    }>(ApiRoutes.Auth.GetConnectedProviders, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const disconnectProvider = async (provider: string, options?: FetchApiOptions) => {
    const response = await fetchApi<{ user: User }>(ApiRoutes.Auth.DisconnectProvider(provider), {
      method: 'DELETE',
      ...options,
    });
    if (response.success) {
      mutate();
    }
    console.log('disconnectProvider', response.data);
    return response;
  };

  const getRegistrationProgress = async (options?: FetchApiOptions) => {
    const response = await fetchApi<RegistrationProgress>(ApiRoutes.Auth.GetRegistrationProgress, {
      method: 'GET',
      ...options,
    });
    console.log('getRegistrationProgress', response.data);
    return response;
  };

  const updatePersonalInfo = async (input: UpdatePersonalInfoInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{ user: User; registrationStep: string }>(
      ApiRoutes.Auth.UpdatePersonalInfo,
      {
        data: input,
        ...options,
      }
    );
    if (response.success && response.data?.user) {
      mutate();
    }
    console.log('updatePersonalInfo', response.data);
    return response;
  };

  const updateAddress = async (input: UpdateAddressInput, options?: FetchApiOptions) => {
    const response = await fetchApi<{
      user: User;
      registrationStep: string;
      onboardingStep: string;
    }>(ApiRoutes.Auth.UpdateAddress, {
      data: input,
      ...options,
    });
    if (response.success && response.data?.user) {
      await mutate(response.data.user, false);
    }
    console.log('updateAddress', response.data);
    return response;
  };

  const updateOnboardingStep = async (
    input: UpdateOnboardingStepInput,
    options?: FetchApiOptions
  ) => {
    const response = await fetchApi<UpdateOnboardingStepResponse>(
      ApiRoutes.Auth.UpdateOnboardingStep,
      {
        data: input,
        ...options,
      }
    );
    if (response.success) {
      mutate();
    }
    console.log('updateOnboardingStep', response.data);
    return response;
  };

  const skipOnboardingStep = async (options?: FetchApiOptions) => {
    const response = await fetchApi<UpdateOnboardingStepResponse>(
      ApiRoutes.Auth.SkipOnboardingStep,
      {
        method: 'POST',
        ...options,
      }
    );
    if (response.success) {
      mutate();
    }
    console.log('skipOnboardingStep', response.data);
    return response;
  };

  return {
    user: user ?? null,
    login,
    register,
    checkEmail,
    logout,
    requestPasswordReset,
    resetPassword,
    validateResetToken,
    verifyEmail,
    resendVerificationEmail,
    googleAuth,
    GetAuthUrl,
    requestLinkingApproval,
    confirmLinking,
    rejectLinking,
    getConnectedProviders,
    disconnectProvider,
    getRegistrationProgress,
    updatePersonalInfo,
    updateAddress,
    updateOnboardingStep,
    skipOnboardingStep,
    initialized: !isLoading,
    mutate,
  };
};

export default useAuth;
